import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Plus, Trash2, BookMarked } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SavedInvoiceItems = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    unit: 'service',
    default_quantity: 1,
    default_unit_price: '',
    default_currency: 'EUR',
  });

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/settings/saved-invoice-items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching saved items:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async () => {
    if (!form.name || !form.description) {
      toast.error(t('invoiceExtra.nameAndDescriptionAreRequired'));
      return;
    }
    try {
      await axios.post(`${API}/api/settings/saved-invoice-items`, {
        ...form,
        default_quantity: parseFloat(form.default_quantity) || 1,
        default_unit_price: form.default_unit_price ? parseFloat(form.default_unit_price) : null,
      });
      toast.success(t('invoiceExtra.itemSaved'));
      setShowAddDialog(false);
      setForm({ name: '', description: '', unit: 'service', default_quantity: 1, default_unit_price: '', default_currency: 'EUR' });
      fetchItems();
    } catch (error) {
      toast.error(t('invoiceExtra.failedToSaveItem'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(t('invoiceExtra.deleteThisSavedItem'))) return;
    try {
      await axios.delete(`${API}/api/settings/saved-invoice-items/${itemId}`);
      toast.success(t('invoiceExtra.itemDeleted'));
      fetchItems();
    } catch (error) {
      toast.error(t('invoiceExtra.failedToDeleteItem'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookMarked className="h-5 w-5 text-[#AA0000]" />
              {t('invoiceExtra.savedInvoiceItems')}
            </CardTitle>
            <CardDescription>{t('invoiceExtra.reusableLineItemsForQuickInvoice')}</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddDialog(true)} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="add-saved-item-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('invoiceExtra.addItem')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-slate-500">{t('invoiceExtra.loading')}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-6 text-slate-400" data-testid="no-saved-items">
            {t('invoiceExtra.noSavedItemsYetAddCommon')}
          </div>
        ) : (
          <Table data-testid="saved-items-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('invoiceExtra.name')}</TableHead>
                <TableHead>{t('invoiceExtra.description')}</TableHead>
                <TableHead>{t('invoiceExtra.unit')}</TableHead>
                <TableHead className="text-right">{t('invoiceExtra.qty')}</TableHead>
                <TableHead className="text-right">{t('invoiceExtra.price')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id} data-testid={`saved-item-${item.id}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="max-w-[250px] truncate">{item.description}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell className="text-right">{item.default_quantity}</TableCell>
                  <TableCell className="text-right">
                    {item.default_unit_price != null ? `${item.default_unit_price} ${item.default_currency}` : '-'}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`delete-saved-item-${item.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-saved-item-dialog">
          <DialogHeader>
            <DialogTitle>{t('invoiceExtra.addSavedInvoiceItem')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('invoiceExtra.shortName')}</Label>
              <Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Consulting Hours" data-testid="saved-item-name" />
            </div>
            <div>
              <Label>{t('invoiceExtra.descriptionAppearsOnInvoice')}</Label>
              <Input value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Technical consulting services" data-testid="saved-item-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>{t('invoiceExtra.unit')}</Label>
                <Select value={form.unit} onValueChange={v => setForm(prev => ({ ...prev, unit: v }))}>
                  <SelectTrigger data-testid="saved-item-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['service', 'hour', 'piece', 'item', 'day'].map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('invoiceExtra.defaultQty')}</Label>
                <Input type="number" step="0.5" value={form.default_quantity}
                  onChange={e => setForm(prev => ({ ...prev, default_quantity: e.target.value }))}
                  data-testid="saved-item-qty" />
              </div>
              <div>
                <Label>{t('invoiceExtra.defaultPrice')}</Label>
                <Input type="number" step="0.01" value={form.default_unit_price}
                  onChange={e => setForm(prev => ({ ...prev, default_unit_price: e.target.value }))}
                  placeholder={t('invoiceExtra.optional')} data-testid="saved-item-price" />
              </div>
            </div>
            <div>
              <Label>{t('invoiceExtra.currency')}</Label>
              <Select value={form.default_currency} onValueChange={v => setForm(prev => ({ ...prev, default_currency: v }))}>
                <SelectTrigger data-testid="saved-item-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['EUR', 'PLN', 'GBP', 'USD', 'DKK', 'SEK', 'NOK', 'CHF', 'CZK'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} data-testid="dialog-cancel-btn">{t('invoiceExtra.cancel')}</Button>
            <Button onClick={handleAdd} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-saved-item-btn">{t('invoiceExtra.saveItem')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SavedInvoiceItems;
